import { DevFeedbackCapturedContext } from "../types/devFeedback";

const MAX_SELECTOR_DEPTH = 5;
const MAX_TEXT_LENGTH = 200;

const isElement = (node: EventTarget | null): node is HTMLElement =>
  !!node && node instanceof HTMLElement;

export const isTypingTarget = (el: Element | null): boolean => {
  if (!el) return false;
  const tag = el.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    (el as HTMLElement).isContentEditable
  );
};

const shortClassName = (el: Element): string => {
  const cls = (el.getAttribute("class") || "")
    .split(/\s+/)
    // Drop CSS-in-JS hashes, and Tailwind variant classes (md:text-sm,
    // hover:bg-gray-100, dark:text-white, ...) — the unescaped ":" makes
    // them invalid inside a querySelector string, which would otherwise
    // throw and make the whole selector unusable for re-locating the
    // element later (e.g. by the feedback overlay).
    .filter((c) => c && !c.startsWith("css-") && !c.includes(":"))
    .slice(0, 2)
    .join(".");
  return cls ? `.${cls}` : "";
};

export const buildElementSelector = (el: Element): string => {
  const parts: string[] = [];
  let current: Element | null = el;
  let depth = 0;

  while (current && current.tagName && depth < MAX_SELECTOR_DEPTH) {
    if (current.id) {
      parts.unshift(`#${current.id}`);
      break;
    }
    const tag = current.tagName.toLowerCase();
    const self: Element = current;
    const parent: Element | null = current.parentElement;
    let nth = "";
    if (parent) {
      const siblings: Element[] = Array.from(parent.children).filter(
        (c: Element) => c.tagName === self.tagName
      );
      if (siblings.length > 1) {
        nth = `:nth-of-type(${siblings.indexOf(self) + 1})`;
      }
    }
    parts.unshift(`${tag}${shortClassName(current)}${nth}`);
    current = parent;
    depth += 1;
  }

  return parts.join(" > ");
};

export const getElementTextSnippet = (el: Element): string | null => {
  const text = (el as HTMLElement).innerText?.trim() || el.textContent?.trim() || "";
  if (!text) return null;
  return text.length > MAX_TEXT_LENGTH ? `${text.slice(0, MAX_TEXT_LENGTH)}…` : text;
};

const HEADING_SELECTOR = "h1,h2,h3,h4,h5,h6,[role='heading']";
const LANDMARK_SELECTOR = "header,nav,form,table,section,aside,[role],[data-feedback-label]";

export const buildComponentLabel = (el: Element): string | null => {
  let current: Element | null = el;
  let depth = 0;

  while (current && depth < 8) {
    const explicit = current.getAttribute?.("data-feedback-label");
    if (explicit) return explicit;
    current = current.parentElement;
    depth += 1;
  }

  const heading = el.closest(HEADING_SELECTOR);
  if (heading) {
    const text = getElementTextSnippet(heading);
    if (text) return text.slice(0, 80);
  }

  const landmark = el.closest(LANDMARK_SELECTOR);
  if (landmark) {
    const tag = landmark.tagName.toLowerCase();
    return `${tag}${shortClassName(landmark)}`;
  }

  return null;
};

export const buildCapturedContext = (
  target: HTMLElement,
  event: MouseEvent
): DevFeedbackCapturedContext => {
  const rect = target.getBoundingClientRect();
  return {
    page_url: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title || null,
    component_label: buildComponentLabel(target),
    element_selector: buildElementSelector(target),
    element_tag: target.tagName.toLowerCase(),
    element_text: getElementTextSnippet(target),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    meta: {
      scrollX: Math.round(window.scrollX),
      scrollY: Math.round(window.scrollY),
      // Viewport-relative click position — combined with scrollX/Y above this
      // gives an absolute page position, used as a fallback anchor when
      // element_selector no longer resolves on revisit (layout/content changed).
      clickX: Math.round(event.clientX),
      clickY: Math.round(event.clientY),
      // Clicked element's own box at capture time, so a marker can be
      // anchored to "where this element was" even if the selector goes stale.
      elementRect: {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
        height: Math.round(rect.height),
      },
    },
  };
};

export const resolveTripleClickTarget = (event: MouseEvent): HTMLElement | null => {
  if (event.detail !== 3) return null;
  const target = event.target;
  if (!isElement(target)) return null;
  return target;
};
